<template>
  <div class="tab-container">
    <div class="tab-section">
      <div class="title">{{ $t("app.campaign.settings.tabs.info.title") }}</div>
      <div class="input-inline">
        <VField class="field" :label="$t('app.campaign.settings.tabs.info.campaignName')" :helper="$t('app.campaign.settings.tabs.info.campaignNameInfo')" name="name" :class="{ invalid: campaignNameIsInvalid !== null }" v-model="props.modelValue.campaign.internal_name" @input="(event) => emits('changeName', event)" />
        <div class="input-wrapper">
          <label for="space">{{ $t("app.campaign.settings.tabs.info.spaceName") }}</label>
          <v-select id="space" name="space" label="name" v-model="modelValue.campaign.parent_workspace_id" :reduce="(space) => space.workspace_id" :clearable="false" :options="isSpacesPending ? [] : spaces" :disabled="true">
            <template v-slot:no-options="{ search, searching }">
              <div style="padding: 8px 0">Pas de résultat {{ searching ? ` pour ${search}` : "" }}.</div>
            </template>
          </v-select>
        </div>
        <VField v-if="!!props?.modelValue?.campaign?.locale" class="field" :label="$t('app.campaign.newPage.campaignLanguage.label')" v-tooltip="$t('app.campaign.settings.tabs.info.cannotEditLanguage')" name="language" :modelValue="$t(`other.languages.${props.modelValue.campaign.locale === 'fr-FR' ? 'fr' : 'en'}`)" disabled />
        <VField v-if="!!props?.modelValue?.assessment?.environment" class="field" :label="$t('app.campaign.newPage.recap.environmentSection.title')" v-tooltip="$t('app.campaign.settings.tabs.info.cannotEditEnvironment')" name="environment" :modelValue="$t(`app.campaign.newPage.slidingPanel.environment.${props.modelValue.assessment.environment}.title`)" disabled />
      </div>
      <p v-if="campaignNameIsInvalid" class="red-color bold" style="margin-top: 16px" v-text="getError(campaignNameIsInvalid.name, campaignNameIsInvalid.params)" />
    </div>
    <div class="separator"></div>
    <div class="tab-section">
      <div class="title">{{ $t("app.campaign.settings.tabs.actions.title") }}</div>
      <p class="muted">
        {{ $t("app.campaign.settings.tabs.actions.info") }}
      </p>
      <Button class="primary" :label="$t('app.campaign.settings.tabs.actions.title')" :disabled="!canArchive" @click="state.showActionModal = true" />
      <p v-if="!canArchive" class="muted" v-text="$t('app.campaign.settings.tabs.actions.notPossible')" />
    </div>
    <ActionModal modal="archive" :data="modelValue" :isOpen="state.showActionModal" @close="state.showActionModal = false" @confirm="confirm" />
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.tab-container {
  .campaign-card {
    margin: 16px auto;
  }

  .separator {
    width: 100%;
    height: 1px;
    border-top: 1px solid var(--light-grey-color);

    margin: 16px 0 16px 0;
  }

  .tab-section {
    .field {
      :deep(.helper-text) {
        color: $secondary-400-color !important;
      }
    }
    .title {
      font-family: $bioSans;
      font-weight: 700;
      font-size: 20px;
      margin-bottom: 6px;
    }

    .toggle-container {
      margin-top: 16px;
    }

    .date {
      margin: 16px 0;

      .bold {
        text-transform: uppercase;
        margin-bottom: 4px;
      }
    }

    .input-inline {
      margin-top: 16px;
    }

    .btn {
      margin: 16px 0;
    }
  }
}
</style>

<script setup lang="ts">
import { useRoute } from "vue-router";
import { computed } from "vue";
import { getError } from "@/composables/error.js";
import { Campaign } from "@/types/campaign";

import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import { useParticipations } from "@/queries/participant.query";
import { useSpaces } from "@/queries/space.query";

const route = useRoute();

const { data: participations, isPending } = useParticipations();

const { data: spaces, isPending: isSpacesPending } = useSpaces();

const emits = defineEmits<{
  (e: "changeName", value: string): void;
  (e: "archive"): void;
  (e: "toggleError", value: { name: string; params?: Record<string, any> } | null): void;
  (e: "update:modelValue", value: Campaign): void;
}>();

const props = defineProps<{
  modelValue: Campaign,
}>();

const state = reactive({
  showActionModal: false,
});

const campaignNameIsInvalid = computed(() => {
  if (!props.modelValue.campaign.internal_name) {
    emits("toggleError", {
      name: "ERROR_REQUIRED_FIELD",
    });
    return { name: "ERROR_REQUIRED_FIELD" };
  }

  if (props.modelValue.campaign.internal_name.length > 50) {
    emits("toggleError", {
      name: "ERROR_TOO_LONG",
      params: { size: 50 },
    });
    return { name: "ERROR_TOO_LONG", params: { size: 50 } };
  }

  emits("toggleError", null);
  return null;
});

const canArchive = computed(() => {
  if (isPending.value) return false;
  if (participations.value.filter((p) => p.state === 1).length < 1) return true;

  return false;
});

function confirm() {
  state.showActionModal = false;
  emits("archive");
}
</script>
